// apps/web/src/pages/Dashboard.tsx
import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  alpha,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  People as PeopleIcon,
  FitnessCenter as FitnessCenterIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

const Dashboard: React.FC = () => {
  const stats = [
    {
      title: 'Всего посетителей',
      value: '156',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#3B82F6',
      progress: 75,
    },
    {
      title: 'Активные тренировки',
      value: '12',
      icon: <FitnessCenterIcon sx={{ fontSize: 40 }} />,
      color: '#10B981',
      progress: 60,
    },
    {
      title: 'Посещений за месяц',
      value: '342',
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      color: '#8B5CF6',
      progress: 85,
    },
    {
      title: 'Выручка за месяц',
      value: '₽89,400',
      icon: <PaymentIcon sx={{ fontSize: 40 }} />,
      color: '#F59E0B',
      progress: 40,
    },
  ];

  const recentActivities = [
    { time: '10:30', visitor: 'Иван Иванов', action: 'Вход в зал', detail: 'Терминал 1' },
    {
      time: '10:25',
      visitor: 'Мария Петрова',
      action: 'Покупка абонемента',
      detail: 'Премиум, 6 мес',
    },
    { time: '10:15', visitor: 'Алексей Сидоров', action: 'Выход из зала', detail: 'Время: 1ч 25м' },
    {
      time: '09:45',
      visitor: 'Елена Ковалева',
      action: 'Запись на тренировку',
      detail: 'Йога, 19:00',
    },
    { time: '09:30', visitor: 'Дмитрий Волков', action: 'Оплата', detail: '₽5,000' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
        Дашборд
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Обзорная панель управления фитнес-центром
      </Typography>

      {/* Карточки статистики */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(stat.color, 0.1)}`,
                },
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(stat.color, 0.1),
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography variant="h3" fontWeight={700}>
                    {stat.value}
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {stat.title}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <LinearProgress
                    variant="determinate"
                    value={stat.progress}
                    sx={{
                      flexGrow: 1,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(stat.color, 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: stat.color,
                      },
                    }}
                  />
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {stat.progress}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Последние активности */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h6" fontWeight={600}>
                Последние активности
              </Typography>
              <HistoryIcon color="action" />
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Время</TableCell>
                    <TableCell>Посетитель</TableCell>
                    <TableCell>Действие</TableCell>
                    <TableCell>Детали</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActivities.map((activity, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{activity.time}</TableCell>
                      <TableCell>{activity.visitor}</TableCell>
                      <TableCell>{activity.action}</TableCell>
                      <TableCell>{activity.detail}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Быстрый доступ
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <PeopleIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2">Добавить посетителя</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Быстрая регистрация
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <FitnessCenterIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2">Создать тренировку</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Расписание занятий
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ReceiptIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2">Финансовые отчеты</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Аналитика доходов
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
